import { computed, DOCUMENT, effect, inject, Inject, Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { HttpService } from '@shared/service/http.service';

import { Title } from '@angular/platform-browser';
import { EInterfaceInputStyle, EInterfaceSize } from '@shared/enums/EInterface';
import { IInterface } from '@shared/models/IInterface';
import { AuthApiService } from '@shared/api/auth-api/auth-api.service';
import { IApiResponse } from '@shared/models/IApiResponse';
import { UserService } from '@shared/service/user.service';
import { ITenant } from '@shared/models/ITenant';
import { UserStore } from '@shared/store/user.store';
import { UiConfigStore } from '@shared/store/ui-config.store';
import { StorageService } from '@shared/service/storage.service';
import { STORAGE_KEYS } from '@shared/constants/StorageKeys';
import { firstValueFrom } from 'rxjs';
import { LoaderService } from '@shared/service/loader.service';
import { finalize } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class ConfigService {
  private readonly _storage = inject(StorageService);
  private readonly _http = inject(HttpService);
  private readonly _api = inject(AuthApiService);
  private readonly _user = inject(UserService);
  private readonly _storeUser = inject(UserStore);
  private readonly _storeUi = inject(UiConfigStore);
  private readonly _title = inject(Title);
  private readonly _loader = inject(LoaderService);

  readonly isDark = this._storeUi.isDark;
  readonly config = this._storeUi.config;
  readonly inputStyle = this._storeUi.inputStyle;

  readonly layout = computed(() => this._storeUser.userInfo()?.layout || this._storeUi.configLayout());
  readonly size = computed(() => this._storeUser.userInfo()?.size || this._storeUi.configSize());

  private readonly exclude = new Set([
    'menuLogoUrl',
    'frontLogoUrl',
    'menuLogoDarkThemeUrl',
    'frontText',
    'frontFootNote',
    'frontLogoDarkThemeUrl',
    'size',
    'darkTheme',
    'frontFootNoteMobile',
    'inputStyle',
    'layout',
    'menuMode',
    'theme',
    'frontTextMobile',
    'loginLeftBackgroundImg',
    'loginLeftBackgroundImgUrl',
    'loginLeftImg',
    'loginLeftImgUrl',
    'toastPosition',
  ]);
  private readonly fontSizeMap = {
    [EInterfaceSize.small]: '12px',
    [EInterfaceSize.medium]: '14px',
    [EInterfaceSize.large]: '15px',
  };
  private rerender: Renderer2;

  constructor(
    rendererFactory: RendererFactory2,
    @Inject(DOCUMENT) private document: Document
  ) {
    effect(() => {
      this.document.documentElement.style.fontSize = this.fontSizeMap[this.size()] ?? '14px';
    });
    effect(() => {
      this.document.documentElement.classList.toggle('dark', this.isDark());
    });
    effect(() => {
      const filled = this.inputStyle() === EInterfaceInputStyle.filled;
      this.document.documentElement.classList.toggle('ui-input-filled', filled);
      this.document.documentElement.classList.toggle('ui-input-outlined', !filled);
    });
    this.rerender = rendererFactory.createRenderer(null, null);
  }

  async loadTenant(): Promise<ITenant> {
    const subdomain = this.getSubDomain();

    this._storeUi.setShowOneLogin(['wwb'].includes(subdomain));

    const res = await firstValueFrom<IApiResponse<ITenant>>(this._http.get('common/getTenant', { subdomain }));

    if (res?.success && res.data) {
      const { uiSettings, name, msSigninEnabled, msSigninMandatory } = res.data;

      this.initConfig(uiSettings);

      if (name !== '(default)') this._title.setTitle(name);

      this._storeUi.setMsSigninEnabled(msSigninEnabled);
      this._storeUi.setMsSigninMandatory(msSigninMandatory);

      return res.data;
    }

    throw new Error('Server error');
  }

  switchThemeMode() {
    const isDark = !this.isDark();
    this._loader.show();
    this._api
      .profileThemeUpdate(isDark)
      .pipe(finalize(() => this._loader.hide()))
      .subscribe(() => {
        const user = this._storeUser.user();
        user.userInfo.useDarkTheme = isDark;
        this._user.updateUser(user);
        this.updateTheme();
      });
  }

  updateTheme() {
    const dark = this.isDark() ?? this._storage.get<boolean>(STORAGE_KEYS.DARK_MODE) ?? false;

    this._storage.set<boolean>(STORAGE_KEYS.DARK_MODE, dark);
    this.applyStyles();
  }

  private initConfig(config: IInterface) {
    this._storeUi.setConfig(config);
    this.updateTheme();
  }

  private applyStyles() {
    this.removeExistedStyles();
    if (this.isDark() || !this.config()) return;

    const cssVars = Object.entries(this.config())
      .filter(([key]) => !this.exclude.has(key))
      .map(([k, v]) => `--${k}: ${v};`)
      .join('');

    const style = this.rerender.createElement('style');
    this.rerender.setAttribute(style, 'id', 'domain-variables');
    this.rerender.setAttribute(style, 'type', 'text/css');
    this.rerender.appendChild(style, this.rerender.createText(`:root{${cssVars}}`));

    this.rerender.appendChild(this.document.head, style);
  }

  private removeExistedStyles() {
    const el = this.document.getElementById('domain-variables');
    if (el) this.rerender.removeChild(this.document.head, el);
  }

  private getSubDomain(): string {
    const domain = window.location.hostname;
    let subdomain = '';
    if (domain.indexOf('.') < 0 || domain.split('.')[0] === 'www') {
      subdomain = ' ';
    } else {
      subdomain = domain.split('.')[0];
    }
    if (subdomain === ' ') {
      subdomain = 'dev';
    }
    return subdomain;
  }
}
